import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSector } from '../../contexts/SectorContext';
import {
  fetchTodayV2,
  fetchTodayAiActions,
  fetchRecommendations,
  approveItem,
  rejectApproval,
  fetchAutonomy,
  setAutonomy,
  TodayV2Dto,
  TodayItem,
  RecommendationDto,
} from '../services/MendsVisionService';
import { fetchConnectedChannels, ConnectedChannel } from '../services/connectedChannels';
import { MaiActionCard } from '../components/MaiActionCard';
import { MaiSection } from '../components/MaiSection';
import { MeetingPrepPanel, MeetingPrepTarget } from '../components/MeetingPrepPanel';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import { formatAgentChip } from '../lib/agentDisplay';
import { classifyRecommendation } from '../lib/classifyRecommendation';
import { isTodayDecisionItem, todayApprovalId } from '../lib/todayApprovalId';
import { approveConflictText, friendlyApiMessage, sentence } from '../lib/approvalLabels';
import { readApiError } from '../lib/approvalContent';
import { actionKindLabel } from '../lib/approvalLabels';
import { autonomyDownTarget, lowerAutonomyLevel } from '../lib/autonomyDown';
import { listColumnConditions, saveColumnCondition } from '../../services/CrmAutomationService';
import '../styles/mendsAi.css';

function pillsFor(item: TodayItem) {
  const list: Array<{ label: string; tone?: 'high' | 'you' | 'wait' | 'ai' | 'default' }> = [];
  if (item.priority === 'high') list.push({ label: 'urgente', tone: 'high' });
  if (item.kind === 'ai_off') list.push({ label: 'com você', tone: 'you' });
  if (item.kind === 'ai_attending') list.push({ label: 'IA atendendo', tone: 'ai' });
  if (item.kind === 'awaiting_reply') list.push({ label: 'aguardando', tone: 'wait' });
  if (item.kind === 'approval' || item.kind === 'risk_approval_stale') list.push({ label: 'supervisão', tone: 'ai' });
  if (item.kind === 'risk_silent') list.push({ label: 'risco', tone: 'high' });
  if (item.kind === 'best_next') list.push({ label: 'prioridade', tone: 'you' });
  if (item.kind === 'meeting') list.push({ label: 'reunião', tone: 'default' });
  if (item.kind === 'meeting_now') list.push({ label: 'em andamento', tone: 'wait' });
  if (item.kind === 'meeting_outcome_pending') list.push({ label: 'sem desfecho', tone: 'you' });
  if (item.kind === 'commitment_scheduled') list.push({ label: 'retorno da IA', tone: 'ai' });
  if (item.kind === 'sla_breached' || item.slaBreached) list.push({ label: 'prazo vencido', tone: 'high' });
  if (item.kind === 'ai_no_reply') list.push({ label: 'IA sem resposta', tone: 'high' });
  if (item.kind === 'ai_not_delivered') list.push({ label: 'não entregue', tone: 'high' });
  if (item.kind === 'send_failed') list.push({ label: 'envio falhou', tone: 'high' });
  if (item.kind === 'agent_errors') list.push({ label: 'funcionário com erro', tone: 'high' });
  if (item.kind === 'channel_warning') list.push({ label: 'aviso do canal', tone: 'high' });
  if (item.kind === 'channel_offline') list.push({ label: 'canal offline', tone: 'high' });
  const agent = formatAgentChip(item.agentName, item.agentKind);
  if (agent) list.push({ label: agent, tone: 'ai' });
  return list;
}

function formatTodayLabel(d = new Date()) {
  try {
    const label = d.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    // Só a primeira letra maiúscula ("Sexta-feira, 16 de outubro de 2026") — nunca "De" / "Feira".
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch {
    return d.toLocaleDateString('pt-BR');
  }
}

/** Hora no fuso da agenda (America/Sao_Paulo por padrão), não no fuso do navegador. */
export function formatClock(iso?: string | null, timeZone = 'America/Sao_Paulo') {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone });
  } catch {
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
}

/** "Próximo às 17:30" se for hoje no fuso da agenda; senão com o dia ("Próximo: 19/10 10:00"). */
export function formatNextAgenda(iso?: string | null, timeZone = 'America/Sao_Paulo', now = new Date()) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const day = (x: Date) => {
    try {
      return x.toLocaleDateString('pt-BR', { timeZone, day: '2-digit', month: '2-digit' });
    } catch {
      return x.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }
  };
  const clock = formatClock(iso, timeZone);
  return day(d) === day(now) ? `Próximo às ${clock}` : `Próximo: ${day(d)} ${clock}`;
}

/** Itens de canal offline (checagem ao vivo da tela) no mesmo formato do card "com problema". */
export function channelProblemItems(channels: ConnectedChannel[]): TodayItem[] {
  return channels
    .filter((c) => c.status === 'offline')
    .map((c) => ({
      id: `prob-channel-${c.id}`,
      kind: 'channel_offline',
      title: `${c.label} offline`,
      detail: `${c.detail ? `${c.detail}. ` : ''}Envios e automações neste canal podem falhar até a reconexão.`,
      priority: 'high',
      actionLabel: 'Investigar',
      actionPath: c.href || '/connections',
    }));
}

const DEGRADED_SECTION_LABEL: Record<string, string> = {
  awaiting: 'conversas aguardando',
  approvals: 'decisões',
  operations: 'agenda, problemas e ações da IA',
  conversations: 'operação em andamento',
  agenda_commitments: 'envios e retornos programados',
  risks: 'riscos',
};

/** Aviso honesto quando uma seção não carregou (nunca mostrar vazio como se estivesse tudo certo). */
export function degradedNotice(sections?: string[] | null): string | null {
  const labels = Array.from(new Set((sections || []).map((s) => DEGRADED_SECTION_LABEL[s] || s)));
  if (labels.length === 0) return null;
  return `Não consegui carregar agora: ${labels.join(', ')}. Os números dessas seções podem estar incompletos — tente atualizar.`;
}

const AI_ACTION_SOURCE_LABEL: Record<string, [string, string]> = {
  crm_move: ['card movido no funil', 'cards movidos no funil'],
  meeting: ['reunião marcada', 'reuniões marcadas'],
  follow_up: ['follow-up enviado', 'follow-ups enviados'],
  commitment: ['retorno cumprido', 'retornos cumpridos'],
  audit: ['ação aprovada executada', 'ações aprovadas executadas'],
  approval: ['ação aprovada executada', 'ações aprovadas executadas'],
};

/** Resumo por tipo ("47 cards movidos no funil · 1 reunião marcada"), somando auditoria e aprovação. */
export function summarizeAiActions(countsBySource: Record<string, number>): Array<{ label: string; count: number }> {
  const counts = new Map<string, { forms: [string, string]; count: number }>();
  Object.entries(countsBySource).forEach(([source, n]) => {
    if (!n) return;
    const forms = AI_ACTION_SOURCE_LABEL[source] || (['outra ação', 'outras ações'] as [string, string]);
    const key = forms[1];
    const cur = counts.get(key);
    counts.set(key, { forms, count: (cur?.count || 0) + n });
  });
  return Array.from(counts.values())
    .map(({ forms, count }) => ({ label: count === 1 ? forms[0] : forms[1], count }))
    .sort((a, b) => b.count - a.count);
}

function channelStatusLabel(status: ConnectedChannel['status']) {
  if (status === 'online') return 'Online';
  if (status === 'offline') return 'Offline';
  if (status === 'not_configured') return 'Não conectado';
  if (status === 'unavailable') return 'N/D';
  return '?';
}

/** Canais que trazem histórico para montar a base (o convite do onboarding fala só deles). */
const BASE_CHANNELS = new Set(['whatsapp', 'whatsapp-spark', 'email', 'calendar']);

/** Reunião que ainda vai acontecer (ou está acontecendo) com um cliente ligado: dá para preparar. */
function canPrepareMeeting(item: TodayItem): boolean {
  return (item.kind === 'meeting' || item.kind === 'meeting_now') && !!item.contactId;
}

/** Id do evento da agenda no item do today-v2 ("cal-123"). */
function calendarEventId(item: TodayItem): number | null {
  if (!item.id.startsWith('cal-')) return null;
  const n = Number(item.id.slice(4));
  return Number.isFinite(n) && n > 0 ? n : null;
}

const TodayPage: React.FC = () => {
  const { selectedSectorId, sectors } = useSector();
  const navigate = useNavigate();
  const [data, setData] = useState<TodayV2Dto | null>(null);
  const [recs, setRecs] = useState<RecommendationDto[]>([]);
  const [channels, setChannels] = useState<ConnectedChannel[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyDecisionId, setBusyDecisionId] = useState<string | null>(null);
  const [allAiActions, setAllAiActions] = useState<TodayItem[] | null>(null);
  const [aiActionsLoading, setAiActionsLoading] = useState(false);
  const [decisionFlash, setDecisionFlash] = useState<{
    text: string;
    tone: 'ok' | 'error';
    undo?: { label: string; run: () => Promise<void> };
  } | null>(null);
  const [busyAutonomyId, setBusyAutonomyId] = useState<string | null>(null);
  const [prepTarget, setPrepTarget] = useState<MeetingPrepTarget | null>(null);
  /** Listas abertas além do limite da primeira dobra ("Ver todas"). */
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = useCallback(async (refresh = false) => {
    if (!selectedSectorId) {
      setLoading(false);
      setData(null);
      setRecs([]);
      setChannels([]);
      return;
    }
    setLoading(true);
    setError(null);
    setAllAiActions(null);
    // O Hoje aparece assim que o today-v2 chega; recomendações (têm LLM próprio) e canais (checagem ao vivo)
    // preenchem quando chegarem — antes a tela esperava os três.
    const sectorId = Number(selectedSectorId);
    void fetchRecommendations(sectorId, false)
      .then((r) => setRecs(r?.items || []))
      .catch(() => setRecs([]));
    void fetchConnectedChannels(sectorId, (sectors || []).find((s) => s.id === sectorId) || null)
      .then(setChannels)
      .catch(() => setChannels([]));
    try {
      const today = await fetchTodayV2(sectorId, refresh);
      setData(today);
    } catch (e: unknown) {
      setError(apiErrorMessage(e, 'Falha ao carregar o dia'));
    } finally {
      setLoading(false);
    }
  }, [selectedSectorId, sectors]);

  useEffect(() => { void load(false); }, [load]);

  useEffect(() => {
    if (!decisionFlash) return undefined;
    // Com "Desfazer" o aviso fica mais tempo na tela: dá para voltar atrás sem procurar a configuração.
    const t = window.setTimeout(() => setDecisionFlash(null), decisionFlash.undo ? 9000 : 4200);
    return () => window.clearTimeout(t);
  }, [decisionFlash]);

  const go = (path?: string | null) => {
    if (!path) return;
    navigate(path);
  };

  // HJ-04: canal offline entra no card "com problema" (checagem ao vivo, mesma fonte dos chips de canal).
  const problems = useMemo(
    () => [...channelProblemItems(channels), ...(data?.problems || [])],
    [channels, data],
  );
  const problemsTotal = (data?.problemsCount ?? (data?.problems || []).length) + channelProblemItems(channels).length;
  const timeZone = data?.timezone || 'America/Sao_Paulo';

  const showAllAiActions = async () => {
    if (!selectedSectorId) return;
    setAiActionsLoading(true);
    try {
      const all = await fetchTodayAiActions(Number(selectedSectorId));
      setAllAiActions(all.items);
    } catch (e: unknown) {
      setError(apiErrorMessage(e, 'Não consegui carregar todas as ações da IA'));
    } finally {
      setAiActionsLoading(false);
    }
  };

  const onApproveDecision = async (item: TodayItem) => {
    const approvalId = todayApprovalId(item);
    if (!selectedSectorId || !approvalId) return;
    setBusyDecisionId(item.id);
    setDecisionFlash(null);
    try {
      const result = await approveItem(Number(selectedSectorId), approvalId);
      if (result.executionError) {
        setDecisionFlash({
          text: friendlyApiMessage(result.executionError, 'A execução falhou — nada foi enviado.'),
          tone: 'error',
        });
      } else {
        setDecisionFlash({ text: 'Aprovado.', tone: 'ok' });
      }
      await load(true);
    } catch (e: unknown) {
      const err = readApiError(e);
      if (err.status === 409 && (err.code === 'SUPERSEDED' || err.code === 'CONTEXT_UNVERIFIED')) {
        setDecisionFlash({ text: sentence(approveConflictText(err.code, err.reason)), tone: 'error' });
      } else {
        setDecisionFlash({ text: friendlyApiMessage(err.message, 'Não consegui aprovar agora.'), tone: 'error' });
      }
      await load(false);
    } finally {
      setBusyDecisionId(null);
    }
  };

  const onRejectDecision = async (item: TodayItem) => {
    const approvalId = todayApprovalId(item);
    if (!selectedSectorId || !approvalId) return;
    setBusyDecisionId(item.id);
    setDecisionFlash(null);
    try {
      await rejectApproval(Number(selectedSectorId), approvalId);
      setDecisionFlash({ text: 'Sugestão rejeitada.', tone: 'ok' });
      await load(true);
    } catch (e: unknown) {
      setDecisionFlash({
        text: friendlyApiMessage(readApiError(e).message, 'Não consegui rejeitar agora.'),
        tone: 'error',
      });
    } finally {
      setBusyDecisionId(null);
    }
  };

  /**
   * "Baixar autonomia" num card do que a IA fez: desce um degrau o nível do tipo de ação (nunca abaixo de pedir Ok)
   * ou, para movimento do funil, desliga a condição da coluna de destino. Sempre com desfazer.
   */
  const onLowerAutonomy = async (item: TodayItem) => {
    const target = autonomyDownTarget(item);
    const sectorId = Number(selectedSectorId);
    if (!target || !sectorId) return;
    setBusyAutonomyId(item.id);
    setDecisionFlash(null);
    try {
      if (target.kind === 'action') {
        const levels = await fetchAutonomy(sectorId);
        const current = levels.find((l) => l.actionKind === target.actionKind)?.level ?? 'aprovar_antes';
        const next = lowerAutonomyLevel(current);
        const label = actionKindLabel(target.actionKind);
        if (!next) {
          setDecisionFlash({ text: `${label} já pede o seu Ok antes.`, tone: 'ok' });
          return;
        }
        await setAutonomy(sectorId, target.actionKind, next);
        setDecisionFlash({
          text: next === 'aprovar_antes'
            ? `A partir de agora, ${label.toLowerCase()} pede o seu Ok antes.`
            : `A partir de agora, a IA faz ${label.toLowerCase()} e avisa você.`,
          tone: 'ok',
          undo: {
            label: 'Desfazer',
            run: async () => {
              await setAutonomy(sectorId, target.actionKind, current);
              setDecisionFlash({ text: 'Autonomia restaurada.', tone: 'ok' });
            },
          },
        });
      } else {
        const conditions = await listColumnConditions(sectorId);
        const column = conditions.find((c) => c.columnId === target.columnId);
        if (!column || !column.enabled) {
          setDecisionFlash({ text: 'A IA já não move cards para esta etapa sozinha.', tone: 'ok' });
          return;
        }
        const payload = { condition: column.condition, kind: column.kind ?? null };
        await saveColumnCondition(sectorId, target.columnId, { ...payload, enabled: false });
        setDecisionFlash({
          text: `A IA não move mais cards para "${column.columnName}" sozinha. A condição continua salva no funil.`,
          tone: 'ok',
          undo: {
            label: 'Desfazer',
            run: async () => {
              await saveColumnCondition(sectorId, target.columnId, { ...payload, enabled: true });
              setDecisionFlash({ text: `A IA voltou a mover cards para "${column.columnName}".`, tone: 'ok' });
            },
          },
        });
      }
    } catch (e) {
      setDecisionFlash({
        text: friendlyApiMessage(readApiError(e).message, 'Não consegui baixar a autonomia agora.'),
        tone: 'error',
      });
    } finally {
      setBusyAutonomyId(null);
    }
  };

  const renderCard = (item: TodayItem) => {
    const decision = isTodayDecisionItem(item);
    const approvalId = decision ? todayApprovalId(item) : null;
    const busy = busyDecisionId === item.id;

    if (decision && approvalId) {
      // Fundamento vem dos campos estruturados do today-v2 — nunca extraído do texto de `detail`.
      const rule = item.rule?.trim() || null;
      const evidence = item.evidence?.trim() || null;
      const pedido = item.pedido?.trim() || null;
      const valor = item.valor?.trim() || null;
      const hasFundamento = !!(item.contactName || rule || evidence || pedido || valor);
      return (
        <MaiActionCard
          key={item.id}
          title={item.title}
          detail={hasFundamento ? null : item.detail}
          priority={item.priority === 'high' || item.priority === 'low' ? item.priority : 'normal'}
          pills={pillsFor(item)}
          primaryLabel={busy ? '…' : 'Aprovar'}
          onPrimary={() => void onApproveDecision(item)}
          primaryDisabled={busy}
          secondaryLabel="Editar"
          onSecondary={() => navigate(`/central-inteligencia?tab=aprovacoes&id=${approvalId}`)}
          secondaryDisabled={busy}
          tertiaryLabel="Rejeitar"
          onTertiary={() => void onRejectDecision(item)}
          tertiaryDisabled={busy}
          tertiaryClassName="mai-btn-reject"
        >
          <div className="mai-decision-fundamento">
            {item.contactName && (
              <p><span>Cliente</span> {item.contactName}</p>
            )}
            {rule && (
              <p><span>Regra</span> {rule}</p>
            )}
            {pedido && (
              <p><span>Pedido</span> {pedido}</p>
            )}
            {valor && (
              <p><span>Valor</span> {valor}</p>
            )}
            {evidence && (
              <p><span>Evidência</span> {evidence}</p>
            )}
            {item.contactId ? (
              <button
                type="button"
                className="mai-linkish mai-decision-link"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/chat?contactId=${item.contactId}`);
                }}
              >
                Ver conversa
              </button>
            ) : null}
          </div>
        </MaiActionCard>
      );
    }

    if (item.kind === 'ai_action') {
      // HJ-06: quem, o quê, quando e a evidência — prestação de contas, nunca apresentada como aprovação.
      return (
        <MaiActionCard
          key={item.id}
          title={item.title}
          detail={item.detail}
          priority="low"
          pills={[{ label: 'executado', tone: 'ai' }]}
          primaryLabel={item.actionLabel || null}
          onPrimary={item.actionPath ? () => go(item.actionPath) : undefined}
          secondaryLabel={item.secondaryLabel && item.secondaryPath ? item.secondaryLabel : null}
          onSecondary={item.secondaryPath ? () => go(item.secondaryPath) : undefined}
          tertiaryLabel={autonomyDownTarget(item) ? (busyAutonomyId === item.id ? '…' : 'Baixar autonomia') : null}
          onTertiary={() => void onLowerAutonomy(item)}
          tertiaryDisabled={busyAutonomyId === item.id}
        >
          {item.evidence ? (
            <p className="mai-card-detail mai-ai-evidence"><span>Evidência</span> {item.evidence}</p>
          ) : null}
        </MaiActionCard>
      );
    }

    return (
      <MaiActionCard
        key={item.id}
        title={item.title}
        detail={item.detail}
        priority={item.priority === 'high' || item.priority === 'low' ? item.priority : 'normal'}
        pills={pillsFor(item)}
        primaryLabel={item.actionLabel || (item.actionPath ? 'Abrir' : null)}
        onPrimary={item.actionPath ? () => go(item.actionPath) : undefined}
        secondaryLabel={item.secondaryLabel && item.secondaryPath && item.secondaryPath !== item.actionPath
          ? item.secondaryLabel
          : null}
        onSecondary={
          item.secondaryPath && item.secondaryPath !== item.actionPath
            ? () => go(item.secondaryPath)
            : undefined
        }
        tertiaryLabel={canPrepareMeeting(item) ? 'Preparar reunião' : null}
        onTertiary={() => setPrepTarget({
          eventId: calendarEventId(item),
          contactId: item.contactId ?? null,
          title: item.title,
        })}
      />
    );
  };

  const urgent = useMemo(() => data?.urgentConversations || [], [data]);
  const followUps = data?.followUps || [];
  // HJ-03: aprovação parada já está em "Suas decisões" (com prioridade alta) — não repetir como risco.
  const risks = useMemo(
    () => (data?.risks || []).filter((r) => r.kind !== 'risk_approval_stale'),
    [data],
  );
  const best = data?.bestNextActions || [];
  const needsYou = data?.needsYou || [];
  const agenda = data?.agenda || [];
  const aiActions = allAiActions ?? (data?.aiActions?.length ? data.aiActions : (data?.whileYouWereAway || []));
  const aiActionsTotal = data?.aiActionsCount ?? aiActions.length;
  const aiActionsSummary = useMemo(() => {
    if (data?.aiActionCounts) return summarizeAiActions(data.aiActionCounts);
    const bySource: Record<string, number> = {};
    (data?.aiActions || []).forEach((i) => { bySource[i.source || ''] = (bySource[i.source || ''] || 0) + 1; });
    return summarizeAiActions(bySource);
  }, [data]);
  // HJ-05: o KPI usa o número do servidor (itens que começam hoje no fuso da agenda), igual ao "próximo às".
  const agendaTodayCount = data?.agendaTodayCount ?? agenda.length;
  const agendaNextLabel = formatNextAgenda(data?.agendaNextAt ?? null, timeZone);

  /**
   * Contagens a partir do today-v2.
   * "IA atendendo" usa o total real do backend (IA ativa + atividade nas últimas 72h), não a lista cortada.
   * Risco/silêncio fica separado de “aguardando resposta”.
   */
  const ops = useMemo(() => {
    // Totais reais do backend (as listas são cortadas); fallback só para API antiga.
    const awaiting = data?.awaitingReplyCount ?? urgent.filter((i) => i.kind === 'awaiting_reply').length;
    const human = data?.needsHumanCount ?? urgent.filter((i) => i.kind === 'ai_off').length;
    const attending = data?.aiAttendingCount ?? (data?.aiAttending || []).length;
    const silentRisk = data?.riskSilentCount ?? risks.filter((i) => i.kind === 'risk_silent').length;
    // Outros riscos (além do silêncio, que já vem com o total real) — a lista é curta, então contá-la é exato.
    const otherRisks = risks.filter((i) => i.kind !== 'risk_silent').length;
    return { awaiting, human, attending, silentRisk, riskTotal: silentRisk + otherRisks };
  }, [urgent, risks, data]);

  const insightCards = useMemo(() => {
    const cards: Array<{ id: string; tone: 'risk' | 'opp' | 'rec'; title: string; detail: string; path?: string }> = [];
    risks.slice(0, 2).forEach((r) => {
      cards.push({
        id: r.id,
        tone: 'risk',
        title: r.title,
        detail: r.detail || 'Risco operacional detectado.',
        path: r.actionPath || '/central-inteligencia?tab=insights',
      });
    });
    recs.slice(0, 3).forEach((r) => {
      cards.push({
        id: r.id,
        tone: classifyRecommendation(r),
        title: r.title,
        detail: r.why || r.what || r.proposedAction || 'Recomendação da Central.',
        path: r.affectedObjects?.[0]?.path || '/central-inteligencia?tab=insights',
      });
    });
    return cards.slice(0, 3);
  }, [risks, recs]);

  /** Mostra os primeiros itens de uma lista e um "Ver todas" — mantém o essencial do dia numa dobra. */
  const capped = (key: string, list: TodayItem[], limit: number) => {
    const open = !!expanded[key];
    const shown = open ? list : list.slice(0, limit);
    return (
      <>
        {shown.map(renderCard)}
        {list.length > limit && (
          <button
            type="button"
            className="mai-linkish mai-see-all"
            onClick={() => setExpanded((prev) => ({ ...prev, [key]: !open }))}
          >
            {open ? 'Mostrar menos' : `Ver todas (${list.length})`}
          </button>
        )}
      </>
    );
  };

  const dateLabel = formatTodayLabel();
  const approvalsPendingCount =
    data?.approvalsPending ?? needsYou.filter((i) => i.kind === 'approval').length;

  return (
    <div className="mai-page mai-hoje">
      <header className="mai-page-header">
        <div>
          <div className="mai-title-row">
            <h1 className="mai-title">{data?.greeting || 'Bom dia — o que importa agora.'}</h1>
            <button
              type="button"
              className="mai-btn mai-btn-soft"
              disabled={loading || !selectedSectorId}
              onClick={() => void load(true)}
            >
              {loading ? 'Varrendo…' : 'Atualizar'}
            </button>
          </div>
          <p className="mai-subtitle">
            {!selectedSectorId
              ? 'Selecione uma conexão no menu para ver o que precisa de você.'
              : data?.subtitle || (loading ? 'Carregando…' : 'Aqui está o que realmente importa para você hoje.')}
          </p>
          <div className="mai-hoje-meta">
            <span>{dateLabel}</span>
            {typeof data?.actionsDoneToday === 'number' && data.actionsDoneToday > 0 && (
              <span className="mai-pill mai-pill-ai">{data.actionsDoneToday} ações da IA nas últimas 24h</span>
            )}
            {problemsTotal > 0 && (
              <span className="mai-pill mai-pill-high">{problemsTotal} com problema</span>
            )}
            {approvalsPendingCount > 0 && (
              <span className="mai-pill mai-pill-high">{approvalsPendingCount} na fila de aprovação</span>
            )}
          </div>
        </div>
      </header>

      {error && <p className="mai-form-error" role="alert">{error}</p>}
      {degradedNotice(data?.degraded) && (
        <p className="mai-form-error" role="status">{degradedNotice(data?.degraded)}</p>
      )}
      <MeetingPrepPanel
        sectorId={Number(selectedSectorId) || 0}
        target={prepTarget}
        onClose={() => setPrepTarget(null)}
        onOpenChat={(cid) => { setPrepTarget(null); navigate(`/chat?contactId=${cid}`); }}
      />

      {decisionFlash && (
        <div
          className={`mai-toast ${decisionFlash.tone === 'error' ? 'mai-toast-error' : 'mai-toast-ok'}`}
          role={decisionFlash.tone === 'error' ? 'alert' : 'status'}
          aria-live={decisionFlash.tone === 'error' ? 'assertive' : 'polite'}
        >
          {decisionFlash.text}
          {decisionFlash.undo && (
            <button
              type="button"
              className="mai-linkish mai-toast-undo"
              onClick={() => {
                const undo = decisionFlash.undo;
                setDecisionFlash(null);
                void undo?.run().catch((e) => setDecisionFlash({
                  text: friendlyApiMessage(readApiError(e).message, 'Não consegui desfazer agora.'),
                  tone: 'error',
                }));
              }}
            >
              {decisionFlash.undo.label}
            </button>
          )}
        </div>
      )}

      <div className="mai-kpi-strip" aria-label="Resumo do dia">
        {/* Mesmo número da seção "Suas decisões hoje" (aprovações + conversas que dependem de você). */}
        <button
          type="button"
          className="mai-kpi"
          onClick={() => document.getElementById('hoje-decisoes')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        >
          <span className="mai-kpi-icon is-danger" aria-hidden>!</span>
          <div>
            <p className="mai-kpi-value">{needsYou.length}</p>
            <p className="mai-kpi-label">Decisões pendentes</p>
            {approvalsPendingCount > 0 ? (
              <p className="mai-kpi-hint">{approvalsPendingCount} na fila de aprovação</p>
            ) : null}
          </div>
        </button>
        <button
          type="button"
          className="mai-kpi"
          onClick={() => document.getElementById('hoje-agenda')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        >
          <span className="mai-kpi-icon is-info" aria-hidden>◷</span>
          <div>
            <p className="mai-kpi-value">{agendaTodayCount}</p>
            <p className="mai-kpi-label">Compromissos hoje</p>
            {agendaNextLabel ? <p className="mai-kpi-hint">{agendaNextLabel}</p> : null}
          </div>
        </button>
        <button type="button" className="mai-kpi" onClick={() => navigate('/chat?filter=awaiting')}>
          <span className="mai-kpi-icon is-ok" aria-hidden>●</span>
          <div>
            <p className="mai-kpi-value">{ops.awaiting}</p>
            <p className="mai-kpi-label">Conversas em atenção</p>
          </div>
        </button>
        <button type="button" className="mai-kpi" onClick={() => navigate('/central-inteligencia')}>
          <span className="mai-kpi-icon is-warn" aria-hidden>△</span>
          <div>
            <p className="mai-kpi-value">{ops.riskTotal}</p>
            <p className="mai-kpi-label">Riscos em aberto</p>
          </div>
        </button>
      </div>

      <div className="mai-hoje-fold">
        <div className="mai-hoje-col">
          <MaiSection
            id="hoje-problemas"
            label="Com problema"
            count={problemsTotal}
            loading={loading && !data}
            showEmpty={!loading && !!selectedSectorId && problems.length === 0}
            empty="Nenhum problema agora: a IA respondeu, os envios saíram e os canais estão no ar."
            className={problems.length > 0 ? 'mai-problems is-active' : 'mai-problems'}
          >
            {problems.map(renderCard)}
            {problems.length > 0 && problemsTotal > problems.length && (
              <p className="mai-card-detail">Mostrando {problems.length} de {problemsTotal}.</p>
            )}
            <details className="mai-op-note-fold">
              <summary>O que conta como problema</summary>
              <p className="mai-card-detail mai-op-note">
                Só o que está acontecendo agora, nas últimas 24h, e que ninguém resolveu depois:{' '}
                <strong>IA sem resposta</strong> (o turno não gerou texto e nada foi enviado ao cliente depois);{' '}
                <strong>não entregue</strong> (a IA gerou a resposta, passaram 3 min e nada saiu);{' '}
                <strong>envio falhou</strong> (agendamento, retorno, ação aprovada ou campanha registrados como falha, sem reenvio);{' '}
                <strong>funcionário com erro</strong> (2+ conversas sem retorno, ou falha do gateway como falta de crédito);{' '}
                <strong>canal offline</strong> (checagem ao vivo). Se alguém respondeu depois, a conversa foi transferida
                ou a última mensagem não pede resposta (&ldquo;ok&rdquo;, &ldquo;👍&rdquo;), não é problema.
              </p>
            </details>
          </MaiSection>

          <MaiSection
            id="hoje-decisoes"
            label="Suas decisões hoje"
            count={needsYou.length}
            loading={loading}
            showEmpty={!loading && !!selectedSectorId && needsYou.length === 0}
            empty="Nada urgente. A MENDS continua observando."
          >
            {capped('decisions', needsYou, 3)}
          </MaiSection>

          <MaiSection
            label="Conversas prioritárias"
            count={urgent.length}
            showEmpty={!loading && urgent.length === 0}
            empty="Nenhuma conversa em risco agora."
          >
            {capped('urgent', urgent, 3)}
          </MaiSection>
        </div>
        <div className="mai-hoje-col">
          <MaiSection
            id="hoje-agenda"
            label="Agenda: hoje e próximas 24h"
            count={agenda.length}
            showEmpty={!loading && agenda.length === 0}
            empty="Nenhuma reunião ou retorno marcado para hoje e as próximas 24h."
            actions={(
              <button type="button" className="mai-linkish" onClick={() => navigate('/agenda')}>
                Abrir agenda
              </button>
            )}
          >
            {capped('agenda', agenda, 4)}
          </MaiSection>
          <section className="mai-op-panel" aria-label="Operação em andamento">
            <h3>Operação em andamento</h3>
            <div className="mai-op-grid">
              <button
                type="button"
                className="mai-op-stat is-ok"
                onClick={() => navigate('/chat?filter=ai-attending')}
                title="Conversas com IA ativa e movimento nas últimas 72h"
              >
                <strong>{ops.attending}</strong>
                <span>IA atendendo</span>
              </button>
              <button
                type="button"
                className="mai-op-stat is-info"
                onClick={() => navigate('/chat?filter=awaiting')}
                title="O cliente falou por último e ninguém respondeu"
              >
                <strong>{ops.awaiting}</strong>
                <span>Aguardando resposta</span>
              </button>
              <button
                type="button"
                className="mai-op-stat is-warn"
                onClick={() => navigate('/chat?filter=needs-human')}
                title="Aguardando resposta com a IA pausada — só uma pessoa responde"
              >
                <strong>{ops.human}</strong>
                <span>Precisam de humano</span>
              </button>
              <button
                type="button"
                className="mai-op-stat is-danger"
                onClick={() => navigate('/chat?filter=risk-silent')}
                title="Conversas paradas entre 4 e 30 dias"
              >
                <strong>{ops.silentRisk}</strong>
                <span>Risco / silêncio</span>
              </button>
            </div>
            {channels.some((c) => c.status === 'not_configured' && BASE_CHANNELS.has(c.id)) && (
              <p className="mai-card-detail mai-channels-invite">
                Conecte {channels.filter((c) => c.status === 'not_configured' && BASE_CHANNELS.has(c.id)).map((c) => c.label).join(', ')} e a MENDS monta a base
                com o histórico.{' '}
                <button type="button" className="mai-linkish" onClick={() => navigate('/comecar')}>Começar</button>
              </p>
            )}
            <details className="mai-op-note-fold">
              <summary>Como contamos cada número e canais conectados</summary>
              <p className="mai-card-detail mai-op-note">
                <strong>IA atendendo:</strong> IA ativa com conversa nas últimas 72h.{' '}
                <strong>Aguardando:</strong> o cliente falou por último (72h) e ninguém respondeu.{' '}
                <strong>Precisam de humano:</strong> aguardando com a IA pausada.{' '}
                <strong>Risco:</strong> conversa parada entre 4 e 30 dias.{' '}
                Cada número abre o Inbox com o mesmo filtro.
              </p>
              <div className="mai-channels" aria-label="Canais conectados">
                <div className="mai-channels-head">
                  <h4>Canais conectados</h4>
                  <span className="mai-card-detail">Status real das integrações deste setor</span>
                </div>
                <div className="mai-channels-row">
                  {channels.length === 0 && !loading && (
                    <span className="mai-card-detail">Selecione um setor para ver canais.</span>
                  )}
                  {channels.map((c) => {
                    // Nunca conectado não é falha: vira convite para o assistente de primeiro acesso.
                    const invite = c.status === 'not_configured';
                    return (
                      <button
                        key={c.id}
                        type="button"
                        className={`mai-channel-chip is-${c.status}${invite ? ' is-invite' : ''}`}
                        title={invite ? `Conectar ${c.label}` : `${c.label}: ${c.detail || channelStatusLabel(c.status)}`}
                        onClick={() => (invite ? navigate('/comecar') : c.href && navigate(c.href))}
                      >
                        <span className="mai-channel-dot" aria-hidden />
                        <span className="mai-channel-label">{c.label}</span>
                        <span className="mai-channel-status">{invite ? 'Conectar' : channelStatusLabel(c.status)}</span>
                        {!invite && c.detail && <span className="mai-channel-detail">{c.detail}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </details>
            <div className="mai-btn-row" style={{ marginTop: 14 }}>
              <button type="button" className="mai-btn mai-btn-soft" onClick={() => navigate('/central-inteligencia?tab=oportunidades')}>
                Ver funil
              </button>
              <button type="button" className="mai-btn mai-btn-ghost" onClick={() => navigate('/central-inteligencia?tab=insights')}>
                Abrir Central de Inteligência
              </button>
            </div>
          </section>
          <MaiSection
            label="Central de Inteligência"
            count={insightCards.length || undefined}
            showEmpty={!loading && insightCards.length === 0}
            empty="Sem insights priorizados. Abra a Central para analisar."
          >
            <div className="mai-insight-stack">
              {insightCards.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`mai-insight-card is-${c.tone}`}
                  onClick={() => go(c.path)}
                >
                  <h4>{c.title}</h4>
                  <p>{c.detail}</p>
                </button>
              ))}
              <button
                type="button"
                className="mai-btn mai-btn-soft"
                onClick={() => navigate('/central-inteligencia?tab=insights')}
              >
                Ver análise completa
              </button>
            </div>
          </MaiSection>
        </div>
      </div>

      <MaiSection
        id="hoje-acoes-ia"
        label="O que a IA fez nas últimas 24h"
        count={aiActionsTotal}
        showEmpty={!loading && aiActions.length === 0}
        empty="A IA não executou nenhuma ação nas últimas 24h."
      >
        {aiActionsSummary.length > 0 && (
          <p className="mai-card-detail mai-ai-summary">
            {aiActionsSummary.map((g) => `${g.count} ${g.label}`).join(' · ')}
          </p>
        )}
        {(allAiActions || expanded.ai ? aiActions : aiActions.slice(0, 5)).map(renderCard)}
        {!allAiActions && !expanded.ai && aiActions.length > 5 && aiActionsTotal <= aiActions.length && (
          <button type="button" className="mai-linkish mai-see-all" onClick={() => setExpanded((p) => ({ ...p, ai: true }))}>
            Ver todas ({aiActions.length})
          </button>
        )}
        {!allAiActions && aiActionsTotal > aiActions.length && (
          <button
            type="button"
            className="mai-btn mai-btn-ghost"
            style={{ marginTop: 12 }}
            disabled={aiActionsLoading}
            onClick={() => void showAllAiActions()}
          >
            {aiActionsLoading ? 'Carregando…' : `Ver todas (${aiActionsTotal})`}
          </button>
        )}
        {approvalsPendingCount > 0 && (
          <button
            type="button"
            className="mai-btn mai-btn-soft"
            style={{ marginTop: 12 }}
            onClick={() => navigate('/central-inteligencia?tab=aprovacoes')}
          >
            Ver fila de aprovação ({approvalsPendingCount})
          </button>
        )}
      </MaiSection>

      <MaiSection
        label="Follow-ups relevantes"
        count={followUps.length}
        showEmpty={!loading && followUps.length === 0}
        empty="Nenhum follow-up exigindo atenção."
      >
        {followUps.map(renderCard)}
      </MaiSection>

      <MaiSection
        label="Próximas melhores ações"
        count={best.length}
        showEmpty={!loading && best.length === 0}
        empty="Sem recomendações priorizadas no momento."
      >
        {best.map(renderCard)}
      </MaiSection>

      <button
        type="button"
        className="mai-register-cta"
        onClick={() => navigate('/registrar')}
      >
        <span>
          + Registrar algo
          <small>Conte o que aconteceu — a MENDS organiza e executa o autorizado</small>
        </span>
        <span aria-hidden>→</span>
      </button>
    </div>
  );
};

export default TodayPage;
